import React from "react";
import { Helmet } from "react-helmet-async";
import { PAGE_META, canonicalFor, SITE } from "../config/site";

/**
 * <Seo path="/about" /> — renders title/description/canonical/OG tags
 * for a given route, pulling from PAGE_META in config/site.js.
 *
 * Falls back to site defaults if the path isn't in PAGE_META.
 */
export default function Seo({ path, title, description, ogImage, breadcrumb, serviceName }) {
  const meta = (path && PAGE_META[path]) || {};
  const finalTitle = title || meta.title || SITE.name;
  const finalDesc = description || meta.description || SITE.description;
  const canonical = path ? canonicalFor(path) : SITE.url;
  const image = ogImage || `${SITE.url}/og/cloudare-default.png`;

  // Optional BreadcrumbList JSON-LD. `breadcrumb` = [{name, path}, ...].
  const breadcrumbLd =
    breadcrumb && breadcrumb.length
      ? {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: breadcrumb.map((b, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: b.name,
            item: b.path ? canonicalFor(b.path) : undefined,
          })),
        }
      : null;

  // Optional Service JSON-LD for service/compliance pages.
  const serviceLd = serviceName
    ? {
        "@context": "https://schema.org",
        "@type": "Service",
        name: serviceName,
        description: finalDesc,
        url: canonical,
        provider: {
          "@type": "Organization",
          name: "Cloudare Technologies",
          url: SITE.url,
        },
        areaServed: ["IN", "AE", "SA", "MY", "FR", "EU", "US"],
      }
    : null;

  return (
    <Helmet>
      <title>{finalTitle}</title>
      <meta name="description" content={finalDesc} />
      <link rel="canonical" href={canonical} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDesc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={image} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDesc} />
      <meta name="twitter:image" content={image} />
      {breadcrumbLd ? (
        <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>
      ) : null}
      {serviceLd ? (
        <script type="application/ld+json">{JSON.stringify(serviceLd)}</script>
      ) : null}
    </Helmet>
  );
}
